import { ReactElement } from 'react';
import './PlaybookTable.scss';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Img } from '@Components/Img';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { BPMTable } from '@Components/StickyTable';
import { CDN } from '@Components/MetricsTable/metrics-table-utils';
import { INC_CHANNEL_SUBPAGES } from './IncrementalityPlaybookShared';
import { equalsIgnoreCase } from '../../utils/text-utils';

const directory = 'incrementality';
const getIcon = (icon: string): ReactElement => (
  <Img src={`${CDN}/${directory}/Icon_${icon}.png`} />
);
const getLogo = (logo: string): ReactElement => (
  <Img src={`${CDN}/${directory}/Logo_${logo}.png`} />
);

const experimentPOCMap = {
  'Brian Binder': 'mailto:brian.binder@tinuiti.com',
  'Chelsea Dyjak': 'mailto:chelsea.dyjak@tinuiti.com',
  'Chris Stokes': 'mailto:chris.stokes.com',
  'Dimond Gooden-Hilton': 'mailto:dimond.gooden@tinuiti.com',
  'Google Incrementality': 'mailto:googleincrementality@tinuiti.com',
  'Incrementality Playbook Slack Channel': 'https://tinuiti.slack.com/archives/C05HHT6JETE',
  'Julia Pelosi': 'mailto:julia,pelosi@tinuiti.com',
  'Maddie Barton': 'mailto:maddie.barton@tinuiti.com',
  "Joe O'Connor": 'mailto:joe.oconnor@tinuiti.com',
  'Bob Avellino': 'mailto:robert.avellino@tinuiti.com',
  'Natasha Blumenkron': 'mailto:natasha.blumenkron@tinuiti.com'
};

const commonExperiments = {
  'Always On Incrementality': ['Incrementality Playbook Slack Channel'],
  'Conversion Lift for Geo': ['Google Incrementality'],
  'Conversion Lift for Users': ['Google Incrementality'],
  'Geo Test': ['Incrementality Playbook Slack Channel'],
  'Ghost Ads': ['Incrementality Playbook Slack Channel'],
  'Match Market Test': ['Incrementality Playbook Slack Channel'],
  'PSA-Geo': ['Incrementality Playbook Slack Channel'],
  'Pulse Test': ['Incrementality Playbook Slack Channel'],
  'Uplift Experiment': ['Google Incrementality'],
  PSA: ['Google Incrementality']
};

const getFlexStyle = (size: number | undefined, count: number) => {
  if (size) return { flex: `${size} ${size} 68px` };
  if (count === 1) return { flex: 1 };
  return {};
};

const buildPlaybookHeaders = (navigate: NavigateFunction) => [
  {
    flex: 19,
    label: 'Channel',
    name: 'Channel',
    nonInteractive: true,
    renderer: row => {
      let subItemIndex = -1;
      return (
        <div className='channel-names'>
          {row.Channel.map(channel => {
            const { label, size, value } = channel;
            subItemIndex++;
            return (
              <div
                className={`channel-name ${row.Channel.length === 1 ? 'single' : ''} ${
                  subItemIndex === 0 ? 'first' : ''
                } ${subItemIndex === row.Channel.length - 1 ? 'last' : ''}`}
                style={getFlexStyle(size, row.Channel.length)}
              >
                {getIcon(value)}
                <div className='label'> {label ?? value}</div>
              </div>
            );
          })}
        </div>
      );
    }
  },
  {
    flex: 10,
    label: 'Platform',
    name: 'Platform',
    nonInteractive: true,
    renderer: row => {
      let subItemIndex = -1;
      return (
        <div className='platforms'>
          {row.Platform.map(platform => {
            const { icon, size, value } = platform;
            subItemIndex++;
            return (
              <div
                className={`platform ${subItemIndex === 0 ? 'first' : ''} ${
                  subItemIndex === row.Platform.length - 1 ? 'last' : ''
                }`}
                style={getFlexStyle(size, row.Platform.length)}
              >
                {icon ? getIcon(value) : getLogo(value)}
              </div>
            );
          })}
        </div>
      );
    }
  },
  {
    flex: 26,
    label: 'Experiment Options',
    name: 'Experiment Options',
    nonInteractive: true,
    renderer: row => {
      let subItemIndex = -1;
      return (
        <div className='experiment-options'>
          {row['Experiment Options'].map(exp => {
            subItemIndex++;
            let currentPlatform;
            let platformIndex = 0;
            const channel = row.Channel[0].label ?? row.Channel[0].value;
            for (const platform of row.Platform ?? []) {
              if (subItemIndex < platformIndex + (platform.size ?? 1)) {
                currentPlatform = platform.value;
                break;
              }
              platformIndex += platform.size ?? 1;
            }
            let experimentLink = exp.experimentLink ?? '';
            if (!experimentLink && channel in INC_CHANNEL_SUBPAGES) {
              const subPages = INC_CHANNEL_SUBPAGES[channel];
              if (subPages?.find(page => equalsIgnoreCase(page, currentPlatform))) {
                experimentLink = `${currentPlatform.replace(/ /g, '')}`;
              } else {
                experimentLink = `${channel.replace(/ /g, '')}`;
              }
            }
            const { experimentName, poc } = commonExperiments[exp]
              ? {
                  experimentName: exp,
                  poc: commonExperiments[exp]
                }
              : exp;
            return (
              <div
                className={`experiment-option ${subItemIndex === 0 ? 'first' : ''} ${
                  subItemIndex === row['Experiment Options'].length - 1 ? 'last' : ''
                }`}
                style={{ flex: 1 }}
              >
                <InteractiveDiv
                  className='experiment-name'
                  onClick={() => navigate(`../${experimentLink}`, { relative: 'path' })}
                >
                  {experimentName}
                </InteractiveDiv>
                <div className='poc-container'>
                  <div className='poc-label'>Internal POC:</div>
                  {poc.map(contact => (
                    <InteractiveDiv
                      className='poc'
                      onClick={() => window.open(experimentPOCMap[contact])}
                    >
                      {contact}
                    </InteractiveDiv>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  },
  {
    flex: 22,
    label: 'Experiment Mode',
    name: 'Experiment Mode',
    nonInteractive: true,
    renderer: row => {
      let subItemIndex = -1;
      return (
        <div className='experiment-modes'>
          {row['Experiment Mode'].map(model => {
            subItemIndex++;
            return (
              <div
                className={`experiment-mode ${row['Experiment Mode'].length === 1 ? 'single' : ''} ${
                  subItemIndex === 0 ? 'first' : ''
                } ${subItemIndex === row['Experiment Mode'].length - 1 ? 'last' : ''}`}
              >
                {getIcon(`ExperimentMode_${model.replace(' ', '')}`)}
                <div className='label'>{model}</div>
              </div>
            );
          })}
        </div>
      );
    }
  },
  {
    flex: 23,
    label: 'Service Model',
    name: 'Service Model',
    nonInteractive: true,
    renderer: row => {
      let subItemIndex = -1;
      return (
        <div className='service-models'>
          {row['Service Model'].map(model => {
            subItemIndex++;
            return (
              <div
                className={`service-model ${row['Service Model'].length === 1 ? 'single' : ''} ${
                  subItemIndex === 0 ? 'first' : ''
                } ${subItemIndex === row['Service Model'].length - 1 ? 'last' : ''}`}
              >
                {getIcon(`ServiceModel_${model.replace(' ', '')}`)}
                <div className='label'>{model}</div>
              </div>
            );
          })}
        </div>
      );
    }
  },
  {
    flex: 12,
    label: 'Effort Score',
    name: 'Effort Score',
    nonInteractive: true,
    renderer: row => {
      let subItemIndex = -1;
      return (
        <div className='effort-scores'>
          {row['Effort Score'].map(effort => {
            subItemIndex++;
            return (
              <div
                className={`effort-score ${row['Effort Score'].length === 1 ? 'single' : ''} ${
                  subItemIndex === 0 ? 'first' : ''
                } ${subItemIndex === row['Effort Score'].length - 1 ? 'last' : ''}`}
              >
                {getIcon(`Lift_${effort}`)}
              </div>
            );
          })}
        </div>
      );
    }
  }
];

export const playbookData = [
  {
    rows: 2,
    Channel: [{ value: 'Search' }],
    Platform: [{ value: 'Google' }],
    'Experiment Options': [
      {
        experimentName: 'Conversion Lift for Users',
        experimentLink: 'GoogleSearch',
        poc: ['Google Incrementality']
      },
      {
        experimentName: 'Conversion Lift for Geo',
        experimentLink: 'GoogleSearch',
        poc: ['Google Incrementality']
      }
    ],
    'Experiment Mode': ['Platform Native', 'Off Platform'],
    'Service Model': ['Managed Service', 'Self Service'],
    'Effort Score': ['Low', 'High']
  },
  {
    rows: 2,
    Channel: [{ label: 'Shopping', value: 'Shoppable' }],
    Platform: [{ value: 'Google' }],
    'Experiment Options': ['Conversion Lift for Users', 'Conversion Lift for Geo'],
    'Experiment Mode': ['Platform Native', 'Off Platform'],
    'Service Model': ['Managed Service', 'Self Service'],
    'Effort Score': ['Low', 'High']
  },
  {
    rows: 4,
    Channel: [{ label: 'Multi Channnel', value: 'MultiChannel' }],
    Platform: [
      { icon: true, size: 2, value: 'GoogleDiscovery' },
      { icon: true, size: 2, value: 'GooglePerformanceMax' }
    ],
    'Experiment Options': [
      {
        experimentName: 'Conversion Lift for Users',
        experimentLink: 'Discovery',
        poc: ['Google Incrementality']
      },
      {
        experimentName: 'Conversion Lift for Geo',
        experimentLink: 'Discovery',
        poc: ['Google Incrementality']
      },
      {
        experimentName: 'Conversion Lift for Users',
        experimentLink: 'PerformanceMax',
        poc: ['Google Incrementality']
      },
      {
        experimentName: 'Conversion Lift for Geo',
        experimentLink: 'PerformanceMax',
        poc: ['Google Incrementality']
      }
    ],
    'Experiment Mode': ['Platform Native', 'Off Platform', 'Platform Native', 'Off Platform'],
    'Service Model': ['Managed Service', 'Self Service', 'Managed Service', 'Self Service'],
    'Effort Score': ['Low', 'High', 'Low', 'High']
  },
  {
    rows: 3,
    Channel: [{ value: 'Display' }],
    Platform: [{ value: 'Google', size: 2 }, { value: 'Tradedesk' }],
    'Experiment Options': [
      {
        experimentName: 'Conversion Lift for Users',
        experimentLink: 'Google(DV360)',
        poc: ['Google Incrementality']
      },
      {
        experimentName: 'Conversion Lift for Geo',
        experimentLink: 'Google(DV360)',
        poc: ['Google Incrementality']
      },
      {
        experimentName: 'Conversion Lift',
        experimentLink: 'TheTradeDesk',
        poc: ['Brian Binder']
      }
    ],
    'Experiment Mode': ['Platform Native', 'Off Platform', 'Platform Native'],
    'Service Model': ['Managed Service', 'Self Service', 'Managed Service'],
    'Effort Score': ['Low', 'High', 'Low']
  },
  {
    rows: 6,
    Channel: [{ value: 'Social' }],
    Platform: [
      { size: 2, value: 'Meta' },
      { value: 'Tiktok' },
      { value: 'Snapchat' },
      { value: 'Pinterest' },
      { value: 'Reddit' }
    ],
    'Experiment Options': [
      {
        experimentName: 'Conversion Lift',
        experimentLink: 'Meta',
        poc: ['Natasha Blumenkron', 'Chris Stokes']
      },
      {
        experimentName: 'Open Source Geo Experiment',
        experimentLink: 'Meta',
        poc: ['Natasha Blumenkron', 'Chris Stokes']
      },
      {
        experimentName: 'Conversion Lift',
        experimentLink: 'TikTok',
        poc: ['Natasha Blumenkron', 'Chelsea Dyjak']
      },
      {
        experimentName: 'Conversion Lift',
        experimentLink: 'Snapchat',
        poc: ['Natasha Blumenkron', 'Julia Pelosi']
      },
      {
        experimentName: 'Conversion Lift',
        experimentLink: 'Pinterest',
        poc: ['Natasha Blumenkron', 'Dimond Gooden-Hilton']
      },
      {
        experimentName: 'Conversion Lift',
        experimentLink: 'Reddit',
        poc: ['Natasha Blumenkron']
      }
    ],
    'Experiment Mode': [
      'Platform Native',
      'Off Platform',
      'Platform Native',
      'Platform Native',
      'Platform Native',
      'Platform Native'
    ],
    'Service Model': [
      'Managed Service',
      'Self Service',
      'Managed Service',
      'Managed Service',
      'Managed Service',
      'Managed Service'
    ],
    'Effort Score': ['Low', 'High', 'Low', 'Low', 'Low', 'Low']
  },
  {
    Channel: [{ label: 'Commerce', value: 'ConnectedCommerce' }],
    Platform: [{ value: 'Amazon' }],
    'Experiment Options': [
      {
        experimentName: 'User Based Testing (Beta)',
        experimentLink: 'Amazon',
        poc: ["Joe O'Connor", 'Bob Avellino']
      }
    ],
    'Experiment Mode': ['Platform Native'],
    'Service Model': ['Managed Service'],
    'Effort Score': ['Medium']
  },
  {
    rows: 7,
    Channel: [{ label: 'Streaming', value: 'StreamingVideo' }],
    Platform: [
      { size: 2, value: 'Youtube' },
      { value: 'Spotify' },
      { icon: true, value: 'PSATesting' },
      { icon: true, value: 'Ghost' },
      { icon: true, value: 'Geotesting' },
      { icon: true, value: 'AlwaysOn' }
    ],
    'Experiment Options': [
      'Conversion Lift for Users',
      'Conversion Lift for Geo',
      {
        experimentName: 'Conversion Lift',
        experimentLink: 'Spotify',
        poc: ['Google Incrementality']
      },
      'PSA',
      'Ghost Ads',
      'PSA-Geo',
      'Always On Incrementality'
    ],
    'Experiment Mode': [
      'Platform Native',
      'Off Platform',
      'Platform Native',
      'Off Platform',
      'Off Platform',
      'Off Platform',
      'Off Platform'
    ],
    'Service Model': [
      'Managed Service',
      'Self Service',
      'Self Service',
      'Managed Service',
      'Managed Service',
      'Managed Service',
      'Managed Service'
    ],

    'Effort Score': ['Low', 'High', 'Low', 'Medium', 'Medium', 'High', 'Medium']
  },
  {
    rows: 2,
    Channel: [{ label: 'Linear TV', value: 'LinearTV' }],
    Platform: [{ value: '' }, { value: '' }],
    'Experiment Options': ['Match Market Test', 'Pulse Test'],
    'Experiment Mode': ['Off Platform', 'Off Platform'],
    'Service Model': ['Managed Service', 'Managed Service'],
    'Effort Score': ['High', 'High']
  },
  {
    Channel: [{ value: 'All Channels' }],
    Platform: [{ icon: true, value: 'Geotesting' }],
    'Experiment Options': ['Geo Test'],
    'Experiment Mode': ['Off Platform'],
    'Service Model': ['Managed Service'],
    'Effort Score': ['High']
  }
];
const REFERENCE_WINDOW_WIDTH = 1920;
const HEADER_HEIGHT = 44;
const BUFFER_HEIGHT = 10;
const DEFAULT_ROW_HEIGHT = 69;
const DEFAULT_ROW_HEIGHT_BY_INDEX = (index, data) =>
  (data && data[index] && data[index].rows ? data[index].rows : 1) * DEFAULT_ROW_HEIGHT + 1;

interface PlaybookTableProps {
  data?: any[];
  headers?: any[];
  rowHeight?: number | ((index: number) => number);
}

export const PlaybookTable = ({ data = playbookData, headers, rowHeight }: PlaybookTableProps) => {
  const navigate = useNavigate();
  const resolvedHeaders = headers ?? buildPlaybookHeaders(navigate);
  const resolvedRowHeight =
    rowHeight ?? ((index: number) => DEFAULT_ROW_HEIGHT_BY_INDEX(index, data));
  const scalingFactor = 1 + (1 - (window.innerWidth ?? 0) / REFERENCE_WINDOW_WIDTH);
  let itemIndex = -1;
  const height =
    BUFFER_HEIGHT +
    HEADER_HEIGHT +
    data
      .map(() => {
        if (typeof resolvedRowHeight === 'number') {
          return resolvedRowHeight * scalingFactor;
        }
        itemIndex++;
        return resolvedRowHeight(itemIndex) * scalingFactor;
      })
      .reduce((a, b) => a + b, 0);
  const scaledRowHeight =
    typeof resolvedRowHeight === 'number'
      ? resolvedRowHeight * scalingFactor
      : (args: number) => resolvedRowHeight(args) * scalingFactor;
  return (
    <div style={{ height, width: '100%' }}>
      <BPMTable
        className='incrementality-playbook-table'
        data={data}
        filterBar={false}
        headerHeight={HEADER_HEIGHT}
        headers={resolvedHeaders}
        rowHeight={scaledRowHeight}
      />
    </div>
  );
};

export const playbookTable = (
  data?: any[],
  headers?: any[],
  rowHeight?: number | ((index: number) => number)
): ReactElement => <PlaybookTable data={data} headers={headers} rowHeight={rowHeight} />;
